import memoize from 'lodash.memoize';
import { cx } from 'emotion';

/**
 * @param {Array<function(...any): void>} handlers the event handler functions
 * @returns {function(...any): void} An event handler to add to an element
 */
function createAggregateEventHandler(handlers) {
  /**
   * This function needed to be moved out of `combineEventHandlers`
   * since babel doesn't handle nested functions using rest operators
   * properly.
   */
  return function aggregateEventHandler() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    handlers.forEach(handler => {
      if (handler) handler(...args);
    });
  };
}
/**
 * Combines multiple event handlers into one
 * @param {...function(...any): void} handlers the event handler functions
 * @returns {function(...any): void} An event handler to add to an element
 */


function combineEventHandlers() {
  for (var _len2 = arguments.length, handlers = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    handlers[_key2] = arguments[_key2];
  }

  return createAggregateEventHandler(handlers);
}

function memoizeCombineEventHandlers() {
  return memoize(combineEventHandlers);
}

/**
 * @typedef {Object} ButtonEventHandlers
 * @property {function(MouseEvent, ...any): void} handleClick
 * @property {function(KeyboardEvent, ...any): void} handleKeyDown
 */

/**
 * @typedef {Object} Options
 * @property {boolean} [preventDefault]
 */
const KEYBOARD_INTERACTIONS = [" ", "Enter"];
/**
 * Create event handlers for native button behavior for non-button elements
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
 *
 * @param {function(MouseEvent|KeyboardEvent, ...any): void} [handler] the event handler function
 * @param {Options} [options]
 * @returns {ButtonEventHandlers}
 */

function createButtonEventHandlers(handler) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!handler) return {};
  const {
    preventDefault = true
  } = options;
  return {
    handleClick: handler,

    handleKeyDown(event) {
      const {
        key
      } = event;
      if (!KEYBOARD_INTERACTIONS.includes(key)) return; // Prevent space key default scrolling behavior

      if (preventDefault) event.preventDefault();

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      handler(event, ...args);
    }

  };
}

function memoizeCreateButtonEventHandlers() {
  return memoize(createButtonEventHandlers);
}

/**
 * Generates a unique ID
 * @param {string} key
 * @returns {string} A generated id
 */
function generateId(key) {
  return `${key}-${Math.floor(Math.random() * 100000, 5)}`;
}

/**
 * Returns a css className that combines what the user passes down and a string.
 * Ideally the string would map to the element's js style object's name.
 * @param {string} className
 * @param {string} appendedString
 * @returns {string} A string that looks like className__appendedString
 */

function createCustomClassNames(className, appendedString) {
  return className && className.split(" ").reduce((acc, cur) => cx(acc, `${cur.trim()}__${appendedString}`), "");
}

export { combineEventHandlers, createButtonEventHandlers, createCustomClassNames, generateId, memoizeCombineEventHandlers, memoizeCreateButtonEventHandlers };
